import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, map, switchMap } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { WawiInvoiceDraftDto } from './gemeinkosten-invoice.models';

export interface WawiBulkEnqueueResult {
    batchId: string;
    jobCount: number;
    jobs: { jobId: string; fileName: string; status: string }[];
}

export interface WawiBulkStatusResult {
    batchId: string;
    totals: {
        total: number;
        queued: number;
        processing: number;
        readyForReview: number;
        committed?: number;
        failed: number;
    };
    jobs: {
        jobId: string;
        fileName: string;
        status: string;
        runId?: string;
        error?: string;
        attemptCount: number;
        maxAttempts: number;
    }[];
}

export interface WawiBulkReadyJob {
    jobId: string;
    batchId: string;
    fileName: string;
    status: string;
    runId?: string;
    createdAt?: string;
    error?: string;
}

export interface WawiBulkReadyResult {
    count: number;
    jobs: WawiBulkReadyJob[];
}

@Injectable({ providedIn: 'root' })
export class GemeinkostenInvoiceKiService {
    private readonly base = 'invoice_ki/';

    constructor(
        private readonly api: KieneBackendApiService,
        private readonly http: HttpClient,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    preview(file: File): Observable<WawiInvoiceDraftDto> {
        const form = new FormData();
        form.append('file', file, file.name);
        const options = {
            headers: new HttpHeaders('Phito-Type: fileupload'),
        };
        return this.api.postFormData(this.apiBaseUrl + this.base + 'invoice_preview.php', form, options);
    }

    bulkEnqueue(files: File[]): Observable<WawiBulkEnqueueResult> {
        const form = new FormData();
        for (const f of files) {
            form.append('files[]', f, f.name);
        }
        const options = {
            headers: new HttpHeaders('Phito-Type: fileupload'),
        };
        return this.api.postFormData(this.apiBaseUrl + this.base + 'invoice_bulk.php', form, options);
    }

    bulkStatus(batchId: string): Observable<WawiBulkStatusResult> {
        return this.api.get(
            this.apiBaseUrl + this.base + 'invoice_bulk_status.php?batchId=' + encodeURIComponent(batchId),
        );
    }

    bulkFailed(limit = 30): Observable<WawiBulkReadyResult> {
        return this.api.get(
            this.apiBaseUrl + this.base + 'invoice_bulk_failed.php?limit=' + encodeURIComponent(String(limit)),
        );
    }

    autoCommit(
        runId: string,
        options?: { honorar?: boolean },
    ): Observable<{ kostenId?: number; kostenIds?: number[] }> {
        const body: Record<string, unknown> = { runId };
        if (options?.honorar) {
            body.honorar = 1;
        }
        return this.api.post(this.apiBaseUrl + this.base + 'invoice_auto_commit.php', body);
    }

    getDraft(runId: string): Observable<WawiInvoiceDraftDto> {
        return this.api.get(this.apiBaseUrl + this.base + 'invoice_get.php?runId=' + encodeURIComponent(runId));
    }

    getPdfFile(runId: string, fileName = 'rechnung.pdf'): Observable<File> {
        return this.api
            .getBlob(this.apiBaseUrl + this.base + 'invoice_pdf.php', new HttpParams().set('runId', runId))
            .pipe(switchMap((blob: Blob) => this.blobToPdfFile(blob, fileName)));
    }

    getKostenPdfFile(kostenId: number, fileName = 'rechnung.pdf', honorar = false): Observable<File> {
        const endpoint = honorar ? 'kosten/read_pdf_honorare.php' : 'kosten/read_pdf_kosten.php';
        return this.api
            .getBlob(this.apiBaseUrl + endpoint, new HttpParams().set('kosten_id', String(kostenId)))
            .pipe(switchMap((blob: Blob) => this.blobToPdfFile(blob, fileName)));
    }

    /** PHP-Fehler/JSON mit HTTP 200 und leere/ungueltige Antworten verwerfen. */
    private blobToPdfFile(blob: Blob, fileName: string): Observable<File> {
        if (!blob || blob.size < 5 || (blob.type && blob.type.includes('json'))) {
            throw new Error('PDF-Antwort ungültig');
        }
        return from(blob.slice(0, 5).arrayBuffer()).pipe(
            map(buf => {
                const magic = String.fromCharCode(...new Uint8Array(buf));
                if (magic !== '%PDF-') {
                    throw new Error('PDF-Antwort ungültig (kein PDF-Header)');
                }
                return new File([blob], fileName, { type: 'application/pdf' });
            }),
        );
    }

    logManualEdit(payload: {
        runId: string;
        kostenId: number;
        typ?: number;
        artikelId?: number;
        lieferantId?: number;
        lieferantName?: string;
        lieferantNameOcr?: string;
        tierarztId?: number;
        rechnungNr?: string;
        datum?: string;
        leistungsdatumVon?: string;
        leistungsdatumBis?: string;
        kommentar?: string;
        /** Snapshot vor Review (Extract / KI-Vorschlag). */
        before?: Record<string, unknown>;
        /** Snapshot nach Speichern. */
        after?: Record<string, unknown>;
        /** Positionen nach Korrektur (denormalisiert für Lern-Lookup). */
        positionen?: Array<{
            position?: number;
            bezeichnung?: string;
            bezeichnungOcr?: string;
            artikelId?: number;
            artikelBezeichnung?: string;
            artikel_me?: string;
            kostenNetto?: number;
            kosten_netto?: number;
            mwstsatz?: number;
            fibuFahrzeugId?: number;
            fibu_fahrzeug_id?: number;
            kennzeichen?: string;
            sachkonto?: string;
            fibuAufwandKontoId?: number;
            fibu_aufwand_konto_id?: number;
            tierarztId?: number;
            tierarzt_id?: number;
            verteilungsart?: string;
        }>;
        preise?: { kostenNetto?: number; kosten_netto?: number; mwstsatz?: number }[];
    }): Observable<any> {
        return this.api.post(this.apiBaseUrl + this.base + 'invoice_correction_log.php', payload);
    }
}

